import * as React from "react";
import { useEffect, useMemo } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useUserSession } from "../../hooks/useUserSession";
import { LoadingPage } from "../LoadingPage/LoadingPage";
import { ErrorPage } from "../ErrorPage/ErrorPage";
import { computeBranchEnforcedPath } from "../../utils/branchPathUtils";
import { useCurrentUserBranch } from "../../hooks/useCurrentUserBranch";
import { useUserAccount } from "../../hooks/useUserAccount";

interface PathParams {
  lltsBranchId?: string;
}

interface Props {
  children: React.ReactNode;
}

/**
 * Ensures that signed-in users browse pages whose URL starts with their LLTS branch id.
 * Users belonging to a non-default branch are redirected to /{branchId}/{rest-of-path}
 * if the URL is missing or has a different branch segment. Users whose branch is the
 * default (id "default") are redirected to a branch-less URL if a branch segment is
 * present.
 *
 * Anonymous users are never redirected by this component (BranchValidator handles
 * invalid branch ids in URLs for them).
 *
 * While the user's branch is being fetched, or while a redirect is pending, a
 * LoadingPage is rendered to prevent flashing the wrong-branch content.
 *
 * Users without an associated branch (e.g. ADMIN / SUPER_USER, for which the
 * branch endpoint returns 401 / 403) are not subject to branch enforcement; the
 * URL they navigated to is left untouched. This is detected via the
 * `getCurrentUserBranchSafe` helper, which returns `null` for those users so the
 * query stays cached as a successful response and does not refetch endlessly.
 */
export const UserBranchEnforcer: React.FC<Props> = ({ children }) => {
  const { username } = useUserSession();
  const { lltsBranchId } = useParams<PathParams>();
  const location = useLocation();
  const history = useHistory();

  const { error: userAccountError } = useUserAccount();
  const { data: userBranch, isLoading: isUserBranchLoading, error: userBranchError } = useCurrentUserBranch();

  const currentFullPath = `${location.pathname}${location.search}${location.hash}`;

  const targetPath = useMemo(() => {
    if (!username || isUserBranchLoading || userBranch === undefined) {
      return undefined;
    }
    if (userBranch === null) {
      return undefined;
    }
    return computeBranchEnforcedPath({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      urlBranchId: lltsBranchId,
      userBranch
    });
  }, [username, isUserBranchLoading, userBranch, location.pathname, location.search, location.hash, lltsBranchId]);

  const redirectNeeded = targetPath !== undefined && targetPath !== currentFullPath;

  useEffect(() => {
    if (redirectNeeded && targetPath) {
      history.replace(targetPath);
    }
  }, [redirectNeeded, targetPath, history]);

  if (username && (userAccountError || userBranchError)) {
    return <ErrorPage apiError={userAccountError || userBranchError} />;
  }

  if (username && (isUserBranchLoading || userBranch === undefined)) {
    return <LoadingPage />;
  }

  if (redirectNeeded) {
    return <LoadingPage />;
  }

  return <>{children}</>;
};
